import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../../contexts/LanguageContext';
import { 
  MessageSquare, Search, Filter, BarChart, Plus,
  AlertCircle, CheckCircle, Clock, ChevronRight,
  FileText, Download, Send, Paperclip, HelpCircle,
  Globe, ArrowRight, Star, Users, Settings, Phone,
  Mail, Calendar, ArrowUpRight, ArrowDownRight,
  Inbox, Archive, Trash, XCircle, ArrowLeft
} from 'lucide-react';
import ChatBot from '../../../components/ChatBot';

const translations = {
  es: {
    title: 'Colaboración de Equipo',
    subtitle: 'Chat, archivos y colaboración con tu equipo',
    sections: {
      chat: 'Chat de Equipo',
      files: 'Archivos',
      calendar: 'Calendario'
    },
    actions: {
      newChat: 'Nuevo Chat',
      uploadFile: 'Subir Archivo',
      aiAssistant: 'Asistente IA',
      back: 'Volver al Dashboard'
    },
    placeholders: {
      message: 'Escribe un mensaje...',
      search: 'Buscar...'
    }
  },
  en: {
    title: 'Team Collaboration',
    subtitle: 'Chat, files, and team collaboration',
    sections: {
      chat: 'Team Chat',
      files: 'Files',
      calendar: 'Calendar'
    },
    actions: {
      newChat: 'New Chat',
      uploadFile: 'Upload File',
      aiAssistant: 'AI Assistant',
      back: 'Back to Dashboard'
    },
    placeholders: {
      message: 'Type a message...',
      search: 'Search...'
    }
  }
};

// Mock messages data
const mockMessages = [
  {
    id: 1,
    author: {
      name: 'Sarah Chen',
      avatar: 'SC',
      role: 'Product Manager'
    },
    content: 'Hey team! Just wanted to share a quick update on the new feature rollout. The initial metrics look promising!',
    timestamp: '10:30 AM',
    reactions: ['👍', '🎉'],
    replies: 2,
    attachments: [
      { name: 'metrics-report.pdf', size: '2.4 MB' }
    ]
  },
  {
    id: 2,
    author: {
      name: 'Alex Kim',
      avatar: 'AK',
      role: 'Developer'
    },
    content: 'Great news! The performance improvements we implemented last week have reduced load times by 40%.',
    timestamp: '10:35 AM',
    reactions: ['🚀', '👏'],
    replies: 1
  },
  {
    id: 3,
    author: {
      name: 'Maria García',
      avatar: 'MG',
      role: 'Designer'
    },
    content: "I've uploaded the new UI mockups for the dashboard redesign. Would love to get your feedback!",
    timestamp: '10:45 AM',
    reactions: ['❤️'],
    replies: 3,
    attachments: [
      { name: 'dashboard-mockups.fig', size: '8.1 MB' }
    ]
  }
];

const TeamCollaborationPage = () => {
  const [showChatbot, setShowChatbot] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const { language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const t = translations[language];

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    // Here you would typically send the message to your backend
    setNewMessage('');
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/dcvee/dashboard')}
              className="flex items-center text-foreground/70 hover:text-foreground"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              {t.actions.back}
            </button>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{t.title}</h1>
              <p className="text-foreground/70">{t.subtitle}</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Globe className="h-5 w-5 text-foreground/70" />
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value as 'en' | 'es')}
                className="bg-secondary/30 border border-border rounded-lg px-3 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
              >
                <option value="en">English</option>
                <option value="es">Español</option>
              </select>
            </div>
            <button
              onClick={() => setShowChatbot(true)}
              className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-foreground hover:bg-secondary/30"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              {t.actions.aiAssistant}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-8">
          {/* Sidebar */}
          <div className="col-span-3 space-y-8">
            {/* Quick Actions */}
            <div className="dark-card p-4">
              <button className="w-full mb-4 inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-accent hover:bg-accent/90">
                <Plus className="h-4 w-4 mr-2" />
                {t.actions.newChat}
              </button>

              <nav className="space-y-1">
                <button className="w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg bg-accent/10 text-accent">
                  <MessageSquare className="h-5 w-5 mr-3" />
                  {t.sections.chat}
                </button>
                <button className="w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg text-foreground hover:bg-secondary/30">
                  <FileText className="h-5 w-5 mr-3" />
                  {t.sections.files}
                </button>
                <button className="w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg text-foreground hover:bg-secondary/30">
                  <Calendar className="h-5 w-5 mr-3" />
                  {t.sections.calendar}
                </button>
              </nav>
            </div>

            {/* Team Members */}
            <div className="dark-card p-4">
              <h3 className="text-sm font-medium text-foreground/70 uppercase tracking-wider mb-4">
                Team Members
              </h3>
              <div className="space-y-2">
                {mockMessages.map(message => (
                  <button
                    key={message.id}
                    className="w-full flex items-center px-3 py-2 rounded-lg text-sm text-foreground hover:bg-secondary/30"
                  >
                    <div className="h-8 w-8 rounded-full bg-accent/10 flex items-center justify-center text-accent">
                      {message.author.avatar}
                    </div>
                    <div className="ml-3 flex-1 text-left">
                      <div className="font-medium">{message.author.name}</div>
                      <div className="text-xs text-foreground/70">{message.author.role}</div>
                    </div>
                    <div className="h-2 w-2 rounded-full bg-green-500"></div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Main Chat Area */}
          <div className="col-span-9">
            <div className="dark-card h-[calc(100vh-12rem)] flex flex-col">
              {/* Chat Header */}
              <div className="p-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Users className="h-5 w-5 text-accent mr-2" />
                    <h2 className="text-lg font-medium text-foreground">{t.sections.chat}</h2>
                  </div>
                  <div className="flex items-center space-x-4">
                    <button className="text-foreground/70 hover:text-foreground">
                      <Search className="h-5 w-5" />
                    </button>
                    <button className="text-foreground/70 hover:text-foreground">
                      <Settings className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {mockMessages.map((message) => (
                  <div key={message.id} className="flex items-start">
                    <div className="h-8 w-8 rounded-full bg-accent/10 flex items-center justify-center text-accent flex-shrink-0">
                      {message.author.avatar}
                    </div>
                    <div className="ml-3 flex-1">
                      <div className="flex items-center">
                        <span className="font-medium text-foreground">{message.author.name}</span>
                        <span className="ml-2 text-xs text-foreground/70">{message.timestamp}</span>
                      </div>
                      <p className="text-foreground mt-1">{message.content}</p>
                      {message.attachments && (
                        <div className="mt-2">
                          {message.attachments.map((attachment, index) => (
                            <div
                              key={index}
                              className="inline-flex items-center px-3 py-1 rounded-lg bg-secondary/30 text-sm"
                            >
                              <FileText className="h-4 w-4 mr-2 text-foreground/70" />
                              <span className="text-foreground">{attachment.name}</span>
                              <span className="ml-2 text-foreground/70">{attachment.size}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {message.reactions && (
                        <div className="mt-2 flex space-x-2">
                          {message.reactions.map((reaction, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-1 rounded-lg bg-secondary/30 text-sm"
                            >
                              {reaction}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Message Input */}
              <div className="p-4 border-t border-border">
                <form onSubmit={handleSendMessage} className="flex items-center space-x-4">
                  <button
                    type="button"
                    className="text-foreground/70 hover:text-foreground"
                  >
                    <Plus className="h-5 w-5" />
                  </button>
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    className="flex-1 bg-secondary/30 border border-border rounded-lg px-4 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                    placeholder={t.placeholders.message}
                  />
                  <button
                    type="button"
                    className="text-foreground/70 hover:text-foreground"
                  >
                    <Paperclip className="h-5 w-5" />
                  </button>
                  <button
                    type="submit"
                    disabled={!newMessage.trim()}
                    className="text-accent hover:text-accent/90 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="h-5 w-5" />
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Chatbot */}
      {showChatbot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-2xl">
            <ChatBot 
              role="support"
              onClose={() => setShowChatbot(false)} 
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamCollaborationPage;